import React from 'react';

/**
 * TextInputModal — generic single-line text-entry dialog.
 *
 * Styled counterpart to `window.prompt()`, matching ConfirmModal so text
 * entry stays consistent with the rest of the console (the browser-native
 * prompt shows the page's hostname and ignores our styling). Named to avoid
 * the existing PromptModal, which views survey "prompt" exchanges — a
 * different, domain-specific concept.
 *
 * Render conditionally — pass falsy for `open` (or simply don't render the
 * component) to hide. Caller owns the open/closed state.
 *
 * @param {boolean} open         - When false, nothing renders.
 * @param {string}  title        - Dialog title. Defaults to "Console Prompt".
 * @param {string|React.ReactNode} message - Optional helper text above the field.
 * @param {string}  label        - Optional label rendered above the input.
 * @param {string}  initialValue - Value the input is seeded with when opened.
 * @param {string}  placeholder  - Input placeholder text.
 * @param {string}  confirmLabel - Label for the confirm button. Defaults to "OK".
 * @param {string}  cancelLabel  - Label for the cancel button. Defaults to "Cancel".
 * @param {Function} onSubmit    - Called with the trimmed value on confirm/Enter.
 * @param {Function} onCancel    - Called on Cancel, Escape, or backdrop click.
 */
export default function TextInputModal({
    open,
    title = 'Console Prompt',
    message,
    label,
    initialValue = '',
    placeholder = '',
    confirmLabel = 'OK',
    cancelLabel = 'Cancel',
    onSubmit,
    onCancel,
}) {
    const [value, setValue] = React.useState(initialValue);
    const inputRef = React.useRef(null);

    // Reseed and focus whenever the dialog opens.
    React.useEffect(() => {
        if (!open) return;
        setValue(initialValue);
        const t = setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
        return () => clearTimeout(t);
    }, [open, initialValue]);

    React.useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    const trimmed = value.trim();
    const submit = () => { if (trimmed) onSubmit?.(trimmed); };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onCancel}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24, maxWidth: 420, width: '90%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#3C4043' }}>{title}</h3>
                {message && (
                    <div style={{ margin: '0 0 16px', fontSize: 14, color: '#5F6368', lineHeight: 1.5 }}>
                        {message}
                    </div>
                )}
                {label && (
                    <label style={{ display: 'block', margin: '0 0 6px', fontSize: 13, color: '#3C4043', fontWeight: 500 }}>
                        {label}
                    </label>
                )}
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    placeholder={placeholder}
                    onChange={e => setValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                    style={{
                        width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                        border: '1px solid #DADCE0', borderRadius: 4, fontSize: 14,
                        color: '#3C4043', outline: 'none', marginBottom: 20,
                    }}
                />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={{
                        padding: '6px 16px', border: '1px solid #DADCE0', borderRadius: 4,
                        background: '#fff', color: '#3C4043', cursor: 'pointer', fontSize: 13,
                    }}>{cancelLabel}</button>
                    <button onClick={submit} disabled={!trimmed} style={{
                        padding: '6px 16px', border: 'none', borderRadius: 4,
                        background: trimmed ? '#1967D2' : '#A8C7FA', color: '#fff',
                        cursor: trimmed ? 'pointer' : 'default', fontSize: 13, fontWeight: 600,
                    }}>{confirmLabel}</button>
                </div>
            </div>
        </div>
    );
}
